import { create } from 'zustand'
import type {
  User,
  KPIMetric,
  Insight,
  DataSource,
  Query,
  Dashboard,
  Service,
  Website,
  MarketingCampaign,
  MarketingContact,
  AiConversation,
  CrmContact,
  BrandingProject,
  VulnerabilityAssessment,
  MobileApp,
  GoogleMapsListing,
  ServiceType,
} from '../types/database'

interface AppState {
  // Auth state
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean

  // Core data state
  kpiMetrics: KPIMetric[]
  insights: Insight[]
  dataSources: DataSource[]
  queries: Query[]
  dashboards: Dashboard[]
  activeDashboard: Dashboard | null

  // Services state
  services: Service[]
  activeService: Service | null

  // Web Design state
  websites: Website[]
  activeWebsite: Website | null

  // Marketing state
  campaigns: MarketingCampaign[]
  activeCampaign: MarketingCampaign | null
  contacts: MarketingContact[]

  // AI Assistant state
  conversations: AiConversation[]
  activeConversation: AiConversation | null

  // CRM state
  crmContacts: CrmContact[]
  activeCrmContact: CrmContact | null

  // Branding state
  brandingProjects: BrandingProject[]
  activeBrandingProject: BrandingProject | null

  // Vulnerability Assessment state
  assessments: VulnerabilityAssessment[]
  activeAssessment: VulnerabilityAssessment | null

  // Mobile App state
  mobileApps: MobileApp[]
  activeMobileApp: MobileApp | null

  // Google Maps state
  googleMapsListings: GoogleMapsListing[]
  activeListing: GoogleMapsListing | null

  // UI state
  sidebarOpen: boolean
  theme: 'light' | 'dark'
  activeView: 'dashboard' | 'services' | 'analytics' | 'settings' | 'pricing' | 'contacts' | 'about' | 'terms' | 'privacy'
  activeServiceType: ServiceType | null

  // Actions - Auth
  setUser: (user: User | null) => void
  setLoading: (loading: boolean) => void
  logout: () => void

  // Actions - Core
  setKpiMetrics: (metrics: KPIMetric[]) => void
  setInsights: (insights: Insight[]) => void
  setDataSources: (sources: DataSource[]) => void
  setQueries: (queries: Query[]) => void
  setDashboards: (dashboards: Dashboard[]) => void
  setActiveDashboard: (dashboard: Dashboard | null) => void

  // Actions - Services
  setServices: (services: Service[]) => void
  setActiveService: (service: Service | null) => void
  addService: (service: Service) => void
  updateService: (id: string, updates: Partial<Service>) => void
  removeService: (id: string) => void

  // Actions - Web Design
  setWebsites: (websites: Website[]) => void
  setActiveWebsite: (website: Website | null) => void
  addWebsite: (website: Website) => void
  updateWebsite: (id: string, updates: Partial<Website>) => void

  // Actions - Marketing
  setCampaigns: (campaigns: MarketingCampaign[]) => void
  setActiveCampaign: (campaign: MarketingCampaign | null) => void
  addCampaign: (campaign: MarketingCampaign) => void
  updateCampaign: (id: string, updates: Partial<MarketingCampaign>) => void
  setContacts: (contacts: MarketingContact[]) => void
  addContact: (contact: MarketingContact) => void

  // Actions - AI Assistant
  setConversations: (conversations: AiConversation[]) => void
  setActiveConversation: (conversation: AiConversation | null) => void
  addConversation: (conversation: AiConversation) => void
  updateConversation: (id: string, updates: Partial<AiConversation>) => void

  // Actions - CRM
  setCrmContacts: (contacts: CrmContact[]) => void
  setActiveCrmContact: (contact: CrmContact | null) => void
  addCrmContact: (contact: CrmContact) => void
  updateCrmContact: (id: string, updates: Partial<CrmContact>) => void
  removeCrmContact: (id: string) => void

  // Actions - Branding
  setBrandingProjects: (projects: BrandingProject[]) => void
  setActiveBrandingProject: (project: BrandingProject | null) => void
  addBrandingProject: (project: BrandingProject) => void

  // Actions - Vulnerability Assessment
  setAssessments: (assessments: VulnerabilityAssessment[]) => void
  setActiveAssessment: (assessment: VulnerabilityAssessment | null) => void
  addAssessment: (assessment: VulnerabilityAssessment) => void

  // Actions - Mobile App
  setMobileApps: (apps: MobileApp[]) => void
  setActiveMobileApp: (app: MobileApp | null) => void
  addMobileApp: (app: MobileApp) => void

  // Actions - Google Maps
  setGoogleMapsListings: (listings: GoogleMapsListing[]) => void
  setActiveListing: (listing: GoogleMapsListing | null) => void
  addGoogleMapsListing: (listing: GoogleMapsListing) => void

  // Actions - UI
  toggleSidebar: () => void
  setTheme: (theme: 'light' | 'dark') => void
  setActiveView: (view: 'dashboard' | 'services' | 'analytics' | 'settings' | 'pricing' | 'contacts' | 'about' | 'terms' | 'privacy') => void
  setActiveServiceType: (type: ServiceType | null) => void
}

export const useAppStore = create<AppState>((set) => ({
  // Initial auth state
  user: null,
  isAuthenticated: false,
  isLoading: true,

  // Initial core state
  kpiMetrics: [],
  insights: [],
  dataSources: [],
  queries: [],
  dashboards: [],
  activeDashboard: null,

  // Initial services state
  services: [],
  activeService: null,

  // Initial web design state
  websites: [],
  activeWebsite: null,

  // Initial marketing state
  campaigns: [],
  activeCampaign: null,
  contacts: [],

  // Initial AI state
  conversations: [],
  activeConversation: null,

  // Initial CRM state
  crmContacts: [],
  activeCrmContact: null,

  // Initial branding state
  brandingProjects: [],
  activeBrandingProject: null,

  // Initial vulnerability assessment state
  assessments: [],
  activeAssessment: null,

  // Initial mobile app state
  mobileApps: [],
  activeMobileApp: null,

  // Initial Google Maps state
  googleMapsListings: [],
  activeListing: null,

  // Initial UI state
  sidebarOpen: true,
  theme: 'light',
  activeView: 'dashboard',
  activeServiceType: null,

  // Auth actions
  setUser: (user) => set({ user, isAuthenticated: !!user }),
  setLoading: (isLoading) => set({ isLoading }),
  logout: () =>
    set({
      user: null,
      isAuthenticated: false,
      kpiMetrics: [],
      insights: [],
      dataSources: [],
      queries: [],
      dashboards: [],
      activeDashboard: null,
      services: [],
      activeService: null,
      websites: [],
      activeWebsite: null,
      campaigns: [],
      activeCampaign: null,
      contacts: [],
      conversations: [],
      activeConversation: null,
      crmContacts: [],
      activeCrmContact: null,
      brandingProjects: [],
      activeBrandingProject: null,
      assessments: [],
      activeAssessment: null,
      mobileApps: [],
      activeMobileApp: null,
      googleMapsListings: [],
      activeListing: null,
    }),

  // Core actions
  setKpiMetrics: (kpiMetrics) => set({ kpiMetrics }),
  setInsights: (insights) => set({ insights }),
  setDataSources: (dataSources) => set({ dataSources }),
  setQueries: (queries) => set({ queries }),
  setDashboards: (dashboards) => set({ dashboards }),
  setActiveDashboard: (activeDashboard) => set({ activeDashboard }),

  // Services actions
  setServices: (services) => set({ services }),
  setActiveService: (activeService) => set({ activeService }),
  addService: (service) =>
    set((state) => ({ services: [...state.services, service] })),
  updateService: (id, updates) =>
    set((state) => ({
      services: state.services.map((s) => (s.id === id ? { ...s, ...updates } : s)),
    })),
  removeService: (id) =>
    set((state) => ({
      services: state.services.filter((s) => s.id !== id),
    })),

  // Web Design actions
  setWebsites: (websites) => set({ websites }),
  setActiveWebsite: (activeWebsite) => set({ activeWebsite }),
  addWebsite: (website) =>
    set((state) => ({ websites: [...state.websites, website] })),
  updateWebsite: (id, updates) =>
    set((state) => ({
      websites: state.websites.map((w) => (w.id === id ? { ...w, ...updates } : w)),
    })),

  // Marketing actions
  setCampaigns: (campaigns) => set({ campaigns }),
  setActiveCampaign: (activeCampaign) => set({ activeCampaign }),
  addCampaign: (campaign) =>
    set((state) => ({ campaigns: [...state.campaigns, campaign] })),
  updateCampaign: (id, updates) =>
    set((state) => ({
      campaigns: state.campaigns.map((c) => (c.id === id ? { ...c, ...updates } : c)),
    })),
  setContacts: (contacts) => set({ contacts }),
  addContact: (contact) =>
    set((state) => ({ contacts: [...state.contacts, contact] })),

  // AI Assistant actions
  setConversations: (conversations) => set({ conversations }),
  setActiveConversation: (activeConversation) => set({ activeConversation }),
  addConversation: (conversation) =>
    set((state) => ({
      conversations: [...state.conversations, conversation],
    })),
  updateConversation: (id, updates) =>
    set((state) => ({
      conversations: state.conversations.map((c) =>
        c.id === id ? { ...c, ...updates } : c
      ),
    })),

  // CRM actions
  setCrmContacts: (crmContacts) => set({ crmContacts }),
  setActiveCrmContact: (activeCrmContact) => set({ activeCrmContact }),
  addCrmContact: (contact) =>
    set((state) => ({ crmContacts: [...state.crmContacts, contact] })),
  updateCrmContact: (id, updates) =>
    set((state) => ({
      crmContacts: state.crmContacts.map((c) =>
        c.id === id ? { ...c, ...updates } : c
      ),
    })),
  removeCrmContact: (id) =>
    set((state) => ({
      crmContacts: state.crmContacts.filter((c) => c.id !== id),
    })),

  // Branding actions
  setBrandingProjects: (brandingProjects) => set({ brandingProjects }),
  setActiveBrandingProject: (activeBrandingProject) =>
    set({ activeBrandingProject }),
  addBrandingProject: (project) =>
    set((state) => ({
      brandingProjects: [...state.brandingProjects, project],
    })),

  // Vulnerability Assessment actions
  setAssessments: (assessments) => set({ assessments }),
  setActiveAssessment: (activeAssessment) => set({ activeAssessment }),
  addAssessment: (assessment) =>
    set((state) => ({
      assessments: [...state.assessments, assessment],
    })),

  // Mobile App actions
  setMobileApps: (mobileApps) => set({ mobileApps }),
  setActiveMobileApp: (activeMobileApp) => set({ activeMobileApp }),
  addMobileApp: (app) =>
    set((state) => ({ mobileApps: [...state.mobileApps, app] })),

  // Google Maps actions
  setGoogleMapsListings: (googleMapsListings) => set({ googleMapsListings }),
  setActiveListing: (activeListing) => set({ activeListing }),
  addGoogleMapsListing: (listing) =>
    set((state) => ({
      googleMapsListings: [...state.googleMapsListings, listing],
    })),

  // UI actions
  toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
  setTheme: (theme) => set({ theme }),
  setActiveView: (activeView) => set({ activeView }),
  setActiveServiceType: (activeServiceType) => set({ activeServiceType }),
}))
